import type {
  FieldObservation,
  FieldObservationType,
} from "@/types/field-observations.types";
import type { AnalysisGroupKind } from "@/utils/plan-analysis/types";

export interface ObservationTypeConfig {
  icon: string;
  color: string;
  /** Clé i18n du libellé (namespace dashboard). */
  labelKey: string;
}

/**
 * Codes visuels des types d'observation (icône Phosphor + couleur), partagés
 * entre l'outil Terrain et le panneau Observations du canvas.
 */
export function getObservationTypeConfig(
  type: FieldObservationType
): ObservationTypeConfig {
  switch (type) {
    case "OVERVIEW":
      return {
        icon: "camera",
        color: "#3b82f6", // blue
        labelKey: "dashboard:fieldObservations.type.OVERVIEW",
      };
    case "DISORDER":
      return {
        icon: "warning",
        color: "#ef4444", // red
        labelKey: "dashboard:fieldObservations.type.DISORDER",
      };
    case "COMMENT":
      return {
        icon: "chat-text",
        color: "#8b5cf6", // purple
        labelKey: "dashboard:fieldObservations.type.COMMENT",
      };
    default:
      return {
        icon: "circle",
        color: "#6b7280",
        labelKey: "",
      };
  }
}

/**
 * Nature d'un groupe d'analyse ↔ type de l'observation qu'il devient : les
 * mêmes trois catégories, sous deux noms.
 */
export const KIND_BY_OBSERVATION_TYPE: Record<
  FieldObservationType,
  AnalysisGroupKind
> = {
  DISORDER: "disorder",
  OVERVIEW: "overview",
  COMMENT: "comment",
};

export const OBSERVATION_TYPE_BY_KIND: Record<
  AnalysisGroupKind,
  FieldObservationType
> = {
  disorder: "DISORDER",
  overview: "OVERVIEW",
  comment: "COMMENT",
};

/** Icône du type, pour la nature d'un groupe d'analyse. */
export function getObservationKindIcon(kind: AnalysisGroupKind): string {
  return getObservationTypeConfig(OBSERVATION_TYPE_BY_KIND[kind]).icon;
}

/**
 * Titre d'une observation dans une liste : son nom, à défaut le libellé de
 * son type. `t` est la fonction de traduction de l'appelant.
 */
export function getObservationTitle(
  observation: Pick<FieldObservation, "name" | "type">,
  t: (key: string) => string
): string {
  const name = observation.name?.trim();
  if (name) return name;
  const { labelKey } = getObservationTypeConfig(observation.type);
  return labelKey ? t(labelKey) : "";
}
